import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

export type User = {
  id: string
  name: string
  roles: string[]
  tenantId?: string | null
  workspaceId?: string | null
}

type AuthContextValue = {
  user: User | null
  isAuthenticated: boolean
  login: (user: User) => void
  logout: () => void
  refreshUser: () => Promise<void>
  org: string | null
  workspace: string | null
  setOrg: (o: string) => void
  setWorkspace: (w: string) => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const getStoredUser = () => {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem('veridoc:user')
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => getStoredUser())
  const [org, setOrg] = useState<string | null>(() => localStorage.getItem('veridoc:org'))
  const [workspace, setWorkspace] = useState<string | null>(() => localStorage.getItem('veridoc:workspace'))

  useEffect(() => {
    const raw = localStorage.getItem('veridoc:user')
    if (raw) setUser(JSON.parse(raw))
  }, [])

  // React to changes made in other tabs/windows
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'veridoc:user') {
        try {
          setUser(e.newValue ? JSON.parse(e.newValue) : null)
        } catch (error) {
          console.warn('Failed to parse user from storage event', error)
        }
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Hydrate session from backend if available
  useEffect(() => {
    if (user) return
    ;(async () => {
      try {
        const { data } = await api.get('/ui/session')
        if (data?.authenticated && data?.user) {
          setUser(data.user)
          localStorage.setItem('veridoc:user', JSON.stringify(data.user))
        }
      } catch (error) {
        console.warn('Failed to hydrate session', error)
      }
    })()
  }, [user])

  useEffect(() => { if (org) localStorage.setItem('veridoc:org', org) }, [org])
  useEffect(() => { if (workspace) localStorage.setItem('veridoc:workspace', workspace) }, [workspace])

  const refreshUser = async () => {
    try {
      const { data } = await api.get('/ui/session')
      if (data?.authenticated && data?.user) {
        setUser(data.user)
        localStorage.setItem('veridoc:user', JSON.stringify(data.user))
      }
    } catch (error) {
      console.warn('Failed to refresh user session', error)
    }
  }

  const value = useMemo(() => ({
    user,
    isAuthenticated: !!user,
    login: (u: User) => { setUser(u); localStorage.setItem('veridoc:user', JSON.stringify(u)) },
    logout: () => {
      setUser(null)
      localStorage.removeItem('veridoc:user')
      // Try full provider logout (will redirect if supported); otherwise just clear tokens
      import('../lib/oidc')
        .then(m => m.logoutOidc(true))
        .catch((error) => console.warn('OIDC logout failed', error))
      try {
        localStorage.removeItem('veridoc:access_token')
        localStorage.removeItem('veridoc:id_token')
        localStorage.removeItem('veridoc:refresh_token')
        localStorage.removeItem('veridoc:token_expires_at')
      } catch (error) {
        console.warn('Failed to clear stored tokens', error)
      }
    },
    refreshUser,
    org,
    workspace,
    setOrg,
    setWorkspace,
  }), [user, org, workspace])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
